import React from 'react';
import { Link } from 'react-router-dom';
import { formatDate, getFieldOfWorkLabel, getInitials, sortQualificationsByIssueDate } from '../../utils';
import { VerificationBadge } from '../../components';
import type {
  Profile,
  WorkExperience,
  Qualification,
  ProfessionalReference,
  ShareableField,
  ProfileSnapshot,
} from '../../types';

const BRAND = '#168c91';
const BRAND_DARK = '#0f172a';
const BRAND_SOFT = '#82d2d1';

const has = (fields: ShareableField[], field: ShareableField) => fields.includes(field);

export interface PassportProfileCardProps {
  fields: ShareableField[];
  profile: Partial<Profile>;
  workExperience: WorkExperience[];
  qualifications: Qualification[];
  references: ProfessionalReference[];
  documents: ProfileSnapshot['documents'];
  bannerRight?: React.ReactNode;
  footerLeft?: React.ReactNode;
}

export const PassportProfileCard: React.FC<PassportProfileCardProps> = ({
  fields,
  profile,
  workExperience,
  qualifications: rawQualifications,
  references,
  documents = [],
  bannerRight,
  footerLeft,
}) => {
  const qualifications = sortQualificationsByIssueDate(rawQualifications);
  const idDocument = documents.find(d => d.document_type === 'id_document');
  const policeClearance = documents.find(d => d.document_type === 'police_clearance');
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ');
  const initials = getInitials(profile.first_name, profile.last_name);

  return (
    <div className="min-h-screen bg-canvas">
      <div className="bg-shell-900 border-b border-shell-800 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img
            src="/favicon.png"
            alt="Passport"
            className="w-7 h-7 rounded-full object-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          <span className="text-sm font-semibold text-white">Passport</span>
        </Link>
        {bannerRight ? (
          <span className="text-xs text-brand-300">{bannerRight}</span>
        ) : null}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm border border-brand-border overflow-hidden">
          <div
            className="px-6 py-7 sm:px-8"
            style={{ background: `linear-gradient(135deg, ${BRAND_DARK} 0%, ${BRAND} 100%)` }}
          >
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
              {has(fields, 'photo') && profile.photo_url ? (
                <img
                  src={profile.photo_url}
                  alt={fullName}
                  className="w-20 h-20 rounded-full object-cover flex-shrink-0 border-[3px] border-white/40"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-white/15 border-[3px] border-white/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-bold text-white">{initials}</span>
                </div>
              )}

              <div className="text-center sm:text-left flex-1">
                {has(fields, 'full_name') && fullName && (
                  <h1 className="text-2xl font-extrabold text-white leading-tight">{fullName}</h1>
                )}
                {profile.field_of_work && (
                  <p className="text-sm font-medium mt-1" style={{ color: BRAND_SOFT }}>
                    {getFieldOfWorkLabel(profile.field_of_work)}
                  </p>
                )}
                {(has(fields, 'email') || has(fields, 'phone')) && (
                  <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-x-5 gap-y-1.5">
                    {has(fields, 'email') && profile.contact_email && (
                      <span className="text-xs text-brand-50/90 inline-flex items-center gap-1.5">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        {profile.contact_email}
                      </span>
                    )}
                    {has(fields, 'phone') && profile.phone && (
                      <span className="text-xs text-brand-50/90 inline-flex items-center gap-1.5">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        {profile.phone}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 py-7 sm:px-8 space-y-7">
            {has(fields, 'bio') && profile.bio && (
              <section>
                <SectionHeading title="Profile Summary" />
                <p className="text-sm text-gray-600 leading-relaxed">{profile.bio}</p>
              </section>
            )}

            {has(fields, 'work_experience') && workExperience.length > 0 && (
              <section>
                <SectionHeading title="Work Experience" />
                <div className="space-y-4">
                  {workExperience.map((exp, i) => (
                    <div key={exp.id ?? i} className="flex gap-4">
                      <div className="flex flex-col items-center pt-1">
                        <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: BRAND }} />
                        {i < workExperience.length - 1 && (
                          <div className="w-0.5 flex-1 bg-gray-200 mt-1" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 pb-1">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <p className="font-bold text-sm text-gray-900">{exp.job_title}</p>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatDate(exp.start_date)} – {exp.is_current ? 'Present' : exp.end_date ? formatDate(exp.end_date) : ''}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 font-medium">
                          {exp.employer}{exp.location ? ` · ${exp.location}` : ''}
                          {exp.is_current && (
                            <span className="ml-2 text-xs bg-brand-50 text-brand-700 border border-brand-200 px-1.5 py-0.5 rounded-full font-semibold">
                              Current
                            </span>
                          )}
                        </p>
                        {exp.description && (
                          <p className="text-sm text-gray-500 mt-2 leading-relaxed">{exp.description}</p>
                        )}
                        {exp.reason_for_leaving && (
                          <div className="mt-2 px-3 py-2 bg-canvas rounded-lg border border-brand-border">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-0.5">Reason for leaving</p>
                            <p className="text-sm text-gray-600">{exp.reason_for_leaving}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {has(fields, 'qualifications') && qualifications.length > 0 && (
              <section>
                <SectionHeading title="Qualifications & Certifications" />
                <div className="space-y-3">
                  {qualifications.map((qual, i) => (
                    <div
                      key={qual.id ?? i}
                      className={`flex gap-4 pb-3 ${i < qualifications.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                      <div className="pt-1">
                        <div className="w-2 h-2 rounded-full flex-shrink-0 bg-brand-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 flex-wrap">
                          <p className="font-bold text-sm text-gray-900">{qual.title}</p>
                          <VerificationBadge status={qual.verification_status} />
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5">{qual.institution}</p>
                        {qual.issue_date && (
                          <p className="text-xs text-gray-400 mt-0.5">Issued: {formatDate(qual.issue_date)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {has(fields, 'references') && references.length > 0 && (
              <section>
                <SectionHeading title="References" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {references.map((ref, i) => (
                    <div key={ref.id ?? i} className="border border-gray-200 rounded-lg px-3.5 py-3 bg-canvas">
                      <p className="font-bold text-sm text-gray-900">{ref.reference_name}</p>
                      {ref.job_title && (
                        <p className="text-xs text-gray-600 mt-0.5">
                          {ref.job_title}{ref.company ? ` · ${ref.company}` : ''}
                        </p>
                      )}
                      {ref.relationship && (
                        <p className="text-xs text-gray-400 italic mt-1">{ref.relationship}</p>
                      )}
                      <VerificationBadge status={ref.verification_status} className="mt-1" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {has(fields, 'official_documents') && (idDocument || policeClearance) && (
              <section>
                <SectionHeading title="Official Documents" />
                <div className="space-y-4">
                  {[
                    { label: 'Identity Document', doc: idDocument },
                    { label: 'Police Clearance', doc: policeClearance },
                  ].map(({ label, doc }) => (
                    doc ? (
                      <div key={label}>
                        <div className="flex items-center justify-between mb-3 gap-2">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{label}</p>
                            <p className="text-xs text-gray-400 truncate max-w-[200px]">{doc.file_name}</p>
                          </div>
                          <VerificationBadge status={doc.verification_status} />
                        </div>
                        {doc.mime_type?.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif)$/i.test(doc.file_name ?? '') ? (
                          <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="block">
                            <img
                              src={doc.file_url}
                              alt={label}
                              className="w-full rounded-xl object-cover max-h-72 border border-gray-100 shadow-sm hover:opacity-95 transition-opacity"
                            />
                          </a>
                        ) : (
                          <a
                            href={doc.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-3 bg-canvas border border-brand-border rounded-xl hover:bg-brand-50 transition-colors"
                          >
                            <svg className="w-5 h-5 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <span className="text-sm text-gray-700 font-medium">View PDF</span>
                            <svg className="w-3.5 h-3.5 text-gray-400 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                      </div>
                    ) : null
                  ))}
                </div>
              </section>
            )}

            <div className="border-t border-gray-200 pt-4 flex flex-wrap items-center justify-between gap-3">
              {footerLeft ? <p className="text-xs text-gray-400">{footerLeft}</p> : <span />}
              <div className="inline-flex items-center gap-2 text-xs text-gray-500">
                <span>Generated by</span>
                <span className="inline-flex items-center gap-1.5">
                  <img
                    src="/wandawazi-logo.png"
                    alt=""
                    className="w-5 h-5 rounded-full object-cover"
                  />
                  <span className="font-extrabold tracking-wider text-brand-600">WANDAWAZI</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHeading: React.FC<{ title: string }> = ({ title }) => (
  <div className="flex items-center gap-2.5 mb-3.5">
    <div className="w-1 h-4 rounded-sm flex-shrink-0" style={{ background: BRAND }} />
    <h2 className="text-[11px] font-extrabold uppercase tracking-wider text-gray-700 m-0">
      {title}
    </h2>
    <div className="flex-1 h-px bg-gray-200" />
  </div>
);
